// components/account/TransactionSuccess/index.tsx
import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../../utils/context/authContext";
import {
  copyToClipboard,
  postUserDetails,
  SetFlag,
} from "../../../utils";
import { FeedbackForm } from "components/forms";
import { PublicSale } from "utils/types/SalesTypes";
import { ensureIso2, formatMoney } from "utils/helper/format";


export const TransactionSuccess: React.FC = () => {
  const { user } = useAuth();
  const [sales, setSales] = useState<PublicSale[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const transactionTimestamp = location.state?.transactionTimestamp || 0;

  const fetchSales = useCallback(
    async (timestamp: number) => {
      if (!user?.userID) return;
      try {
        const resp = await postUserDetails<PublicSale[]>(
          { timestamp },
          "sales-by-timestamp",
          user.userID
        );
        setSales(resp?.data || []);
      } catch (e) {
        console.error("Erreur de chargement des ventes:", e);
      }
    },
    [user]
  );

  useEffect(() => {
    if (user && transactionTimestamp) {
      fetchSales(transactionTimestamp);
    }
  }, [user, transactionTimestamp, fetchSales]);

  return (
    <Box p={4}>
      <Typography variant="h4" gutterBottom>
        Paiement confirmé
      </Typography>

      {sales.length === 0 && (
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Aucune vente correspondante trouvée.
        </Typography>
      )}

      {sales.map((sale) => (
        <Card key={sale.id} sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Vente Id : {sale.id}
            </Typography>

            {/* Items de la vente */}
            {sale.items.map((it, idx) => {
              const iso2 =
                it.countryCode?.toUpperCase() || ensureIso2(it.country);
              return (
                <Box
                  key={idx}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    mb: 1,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    {iso2 && (
                      <SetFlag
                        countryName={it.country || ""}
                        countryCode={iso2}
                      />
                    )}
                    <Box>
                      <Typography sx={{ fontWeight: 600 }}>
                        {it.productName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {iso2 ? iso2 : it.country || "Pays non spécifié"}
                      </Typography>
                    </Box>
                  </Box>

                  <Typography sx={{ minWidth: 120, textAlign: "right" }}>
                    {formatMoney(it.price, it.currency)}
                  </Typography>
                </Box>
              );
            })}

            {/* Codes livrés */}
            {Array.isArray(sale.codes) && sale.codes.length > 0 && (
              <>
                <Typography sx={{ mt: 1 }} color="text.secondary">
                  Vos codes :
                </Typography>
                {sale.codes.map((c, i) => (
                  <Box key={i} sx={{ display: "flex", alignItems: "center" }}>
                    <Typography variant="subtitle1" sx={{ mr: 1 }}>
                      {c}
                    </Typography>
                    <Tooltip title="Copier le code">
                      <IconButton onClick={() => copyToClipboard(c)}>
                        <FileCopyIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
              </>
            )}

            {/* Total & promo */}
            <Box sx={{ mt: 1 }}>
              <Typography>
                Total payé : <strong>{sale.total} BSB Coins</strong>
              </Typography>
              {sale.promoCode && (
                <Typography color="text.secondary">
                  Code promo appliqué : {sale.promoCode}
                  {typeof sale.discountAppliedPct === "number"
                    ? ` (-${sale.discountAppliedPct}%)`
                    : ""}
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>
      ))}

      <div className="w-full flex justify-center items-center">
        <FeedbackForm />
      </div>

      <Button variant="contained" color="primary" onClick={() => navigate("/")}>
        Retour à l’accueil
      </Button>
    </Box>
  );
};
